import * as Comlink from 'comlink';
import type { EngineApi } from './protocol';

let instance: Comlink.Remote<EngineApi> | null = null;
let sink: ((message: string) => void) | null = null;
let pending: string | null = null;

/**
 * Register the sink for worker-level failures: a module that fails to load
 * (a wrong `base` on GitHub Pages, a CSP blocking `worker-src`) or a message
 * that cannot be deserialised. Without this, every Comlink call simply hangs
 * and documents sit at "loading…" with nothing on screen to explain it.
 * A failure raised before the sink is registered is replayed to it.
 */
export function onEngineFatal(fn: (message: string) => void): void {
  sink = fn;
  if (pending) { fn(pending); pending = null; }
}

function fatal(detail: string): void {
  const message = `The image engine failed to start: ${detail} Reload the page.`;
  if (sink) sink(message); else pending = message;
}

/** Every call rejects: used when the Worker constructor itself is unavailable or throws.
 *  Exported so a test can assert it enumerates every `EngineApi` method. */
export function unavailable(): Comlink.Remote<EngineApi> {
  const reject = () => Promise.reject(new Error('the image engine is unavailable in this browser'));
  // The shape matches EngineApi method-for-method; only the Remote<> wrapper
  // (which exists to describe a real MessagePort) needs asserting away.
  const stub: Record<keyof EngineApi, () => Promise<never>> = {
    open: reject, close: reject, run: reject, preview: reject, inspect: reject, overlay: reject, exportPng: reject,
    detectInks: reject, buildSeparation: reject, updateSeparation: reject, platePreview: reject, proofPreview: reject, filmCrop: reject, exportFilms: reject, proofPng: reject,
    mockupLoadTemplate: reject, mockupPrepareEditor: reject, mockupSaveEditor: reject, mockupUnload: reject, mockupRender: reject, mockupBlank: reject, mockupExport: reject, mockupSheet: reject, mockupThumb: reject, mockupPreviewMaps: reject,
    mockupLoadScene: reject, mockupUnloadScene: reject, mockupSceneCover: reject,
  };
  return stub as unknown as Comlink.Remote<EngineApi>;
}

export function engine(): Comlink.Remote<EngineApi> {
  if (!instance) {
    try {
      const worker = new Worker(new URL('./engine.worker.ts', import.meta.url), { type: 'module' });
      worker.addEventListener('error', (e) => fatal(`${e.message || 'the worker script could not be loaded'}.`));
      worker.addEventListener('messageerror', () => fatal('a message could not be delivered to it.'));
      instance = Comlink.wrap<EngineApi>(worker);
    } catch (e) {
      // e.g. Worker is missing entirely, or a blob/module worker is blocked.
      fatal(`${(e as Error).message}.`);
      instance = unavailable();
    }
  }
  return instance;
}

/** Wrap a callback so the worker can call it (Comlink needs proxies for functions). */
export const callback = <T extends (...a: never[]) => void>(fn: T) => Comlink.proxy(fn);

/** Move bytes into the worker without copying. */
export const transferBytes = (bytes: Uint8Array) => Comlink.transfer(bytes, [bytes.buffer]);
